import useLitClients, { ILitClients } from '@/hooks/LitSessionContext/useLitClients';
import useLitSession, { ILitSession } from '@/hooks/LitSessionContext/useLitSession';
import { baseUrl } from '@/lib/const';
import { createContext, useContext, useEffect, useState } from 'react';
import { TypedDataDomain, TypedDataField, ethers } from 'ethers';
import { _TypedDataEncoder, arrayify, hashMessage } from 'ethers/lib/utils';
import * as bitcoinjs from 'bitcoinjs-lib';
import { PKPCosmosWallet } from '@lit-protocol/pkp-cosmos';
import { IRelayPKP } from '@lit-protocol/types';
import { Keypair } from '@solana/web3.js';
import { decryptEncryptedWallet } from '@/lib/auth/decryptEncryptedWallet';
export interface LitSessionContextInterface extends ILitClients, ILitSession {
  bitcoinAddress: string | null;
  cosmosWallet: PKPCosmosWallet | null;
  cosmosAddress: string | null;
  ethersPKPWallet: ({}: {}) => {
    address: string;
    signMessage: (message: string) => Promise<string>;
    _signTypedData: (domain: TypedDataDomain, types: Record<string, Array<TypedDataField>>, value: Record<string, any>) => Promise<string>;
  };
  getEVMEthersv5Wallet: () => Promise<ethers.Wallet | undefined>;
  getSolanaKeypair: () => Promise<Keypair | undefined>;
}
const LitSessionContext = createContext({} as LitSessionContextInterface);
export const redirectUri = `${baseUrl}/home`;
export const LitSessionProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const {
    litNetwork,
    setLitNetwork,
    clients
  } = useLitClients({
    redirectUri
  });
  const [cosmosWallet, setCosmosWallet] = useState<any>(null);
  const [cosmosAddress, setCosmosAddress] = useState<string | null>(null);
  const {
    userInfo,
    litSession,
    PKP,
    setLitSession,
    clearLitSession,
    PKPNetwork,
    connectedWalletAddress,
    activeProviderType,
    setPKPNetwork,
    setAllPKPs,
    allPKPs,
    setPKP,
    currentAuthMethod,
    setCurrentAuthMethod,
    MFAInfo,
    setMFAInfo,
    sessionLength,
    setSessionLength,
    setEmulationPKP,
    userPKP,
    setDecryptedWallets,
    decryptedWallets
  } = useLitSession();
  function GeniusWallet({}: {}) {
    return {
      address: PKP?.ethAddress,
      signMessage: async (message: string): Promise<string> => {
        const client = clients?.litNodeClient;
        const prefixedMessage = hashMessage(message);
        const bytesMessage = arrayify(prefixedMessage);
        const pkpSignRes = await client?.pkpSign({
          toSign: bytesMessage,
          pubKey: PKP.publicKey,
          sessionSigs: litSession
        });
        return pkpSignRes?.signature;
      },
      _signTypedData: async (domain: TypedDataDomain, types: Record<string, Array<TypedDataField>>, value: Record<string, any>): Promise<string> => {
        try {
          const populated = await _TypedDataEncoder.resolveNames(domain, types, value, (name: string) => {
            if (this.provider == null) {
              throw new Error('cannot resolve ENS names without a provider');
            }
            return this.provider.resolveName(name);
          });
          const toSign = _TypedDataEncoder.hash(populated.domain, types, populated.value);
          const toSignBytes = arrayify(toSign);
          if (toSignBytes.length !== 32) {
            throw new Error('hash of typed data is not 32 bytes');
          }
          const client = clients?.litNodeClient;
          const pkpSignRes = await client?.pkpSign({
            toSign: toSignBytes,
            pubKey: PKP.publicKey,
            sessionSigs: litSession
          });
          return pkpSignRes?.signature;
        } catch (e) {
          console.log('ERROR in PKP Signing', e);
          throw e;
        }
      }
    };
  }
  function getBitCoinAddress(pkp: IRelayPKP): string | null {
    try {
      if (pkp) {
        const publicKey = Buffer.from(pkp?.publicKey?.slice(2), 'hex');
        const {
          address
        } = bitcoinjs.payments.p2pkh({
          pubkey: publicKey
        });
        return address;
      } else {
        return null;
      }
    } catch (e) {
      console.log('ERROR in getBitCoinAddress', e.message, pkp);
      return null;
    }
  }
  const bitcoinAddress = getBitCoinAddress(PKP);
  useEffect(() => {
    if (clients?.litNodeClient?.ready && PKP && litSession && !cosmosWallet) {
      getCosmosWallet();
    }
    async function getCosmosWallet() {
      // const pkpClient = new PKPClient({
      //   controllerSessionSigs: litSession,
      //   pkpPubKey: PKP.publicKey,
      //   cosmosAddressPrefix: 'cosmos',
      // });
      // await pkpClient.connect();
      // const pkpCosmosWallet = pkpClient.getCosmosWallet();
      // const etherWallet = pkpClient.getEthWallet();
      // const cosmosAddress = await pkpCosmosWallet.getAddress();
      setCosmosAddress(cosmosAddress);
      // setCosmosWallet(pkpCosmosWallet);
    }
  }, [clients?.litNodeClient, PKP, litSession]);
  const getEVMEthersv5Wallet = async (): Promise<ethers.Wallet | undefined> => {
    try {
      const decryptedEVMWallet = decryptedWallets?.find(w => w.type === 'EVM');
      const evmPrivateKey = await decryptEncryptedWallet({
        PKP: PKP,
        litSession: litSession,
        litNodeClient: clients.litNodeClient,
        encryptedWallet: decryptedEVMWallet
      });
      return evmPrivateKey.wallet;
    } catch (e) {
      console.log('ERROR in getEVMEthersv5Wallet', e);
      return undefined;
    }
  };
  const getSolanaKeypair = async (): Promise<Keypair | undefined> => {
    try {
      const decryptedSolanaWallet = decryptedWallets?.find(w => w.type === 'Solana');
      const solanaKeyPair = await decryptEncryptedWallet({
        PKP: PKP,
        litSession: litSession,
        litNodeClient: clients.litNodeClient,
        encryptedWallet: decryptedSolanaWallet
      });
      return solanaKeyPair.keypair;
    } catch (e) {
      console.log('ERROR in getSolanaKeypair', e);
      return undefined;
    }
  };
  return <LitSessionContext.Provider value={{
    //if wallet is connected directly to front end
    connectedWalletAddress,
    //user info from postgres db
    userInfo,
    //lit session sigs
    litSession,
    //active provider type for session
    activeProviderType,
    //main pkp wallet
    PKP,
    //setting active pkp
    setPKP,
    //manually overriding session if needed
    setLitSession,
    //clearing session
    clearLitSession,
    //ethers based pkp wallet for signing messages with cloudwallet
    ethersPKPWallet: GeniusWallet,
    //evm network for pkp wallet
    PKPNetwork,
    //setting evm network for pkp wallet
    setPKPNetwork,
    //lit network for lit node client
    litNetwork,
    //setting lit network for lit node client
    setLitNetwork,
    //lit node client and auth clients
    clients,
    //setting all pkps
    allPKPs,
    setAllPKPs,
    //current auth method
    currentAuthMethod,
    //setting current auth method
    setCurrentAuthMethod,
    MFAInfo,
    setMFAInfo,
    //bitcoin address
    bitcoinAddress,
    //cosmos wallet
    cosmosWallet,
    //cosmos address
    cosmosAddress,
    //session length
    sessionLength,
    //setting session length
    setSessionLength,
    //if dev account is signed in, set Emulation information for debugging frontend
    setEmulationPKP,
    userPKP,
    setDecryptedWallets,
    decryptedWallets,
    getEVMEthersv5Wallet,
    getSolanaKeypair
  }} data-sentry-element="unknown" data-sentry-component="LitSessionProvider" data-sentry-source-file="LitSessionProvider.tsx">
      {children}
    </LitSessionContext.Provider>;
};
export const useSession = () => {
  const sessionValues = useContext(LitSessionContext);
  return sessionValues;
};